import { AlphaKey, clamp01, ColorKey, error, Gradient, ImageAsset, renderer, Sprite, SpriteFrame, Texture2D, UITransform } from 'cc';
import BaseShaderManipulator from './BaseShaderManipulator';

function generateGradientImage(colorArr: number[], height: number = 1) {
    const pixelsArr: ArrayBufferView = new Uint8Array(colorArr);
    const imageAsset: ImageAsset = new ImageAsset();
    imageAsset.reset({
        _data: pixelsArr,
        width: colorArr.length / height / 4,
        height,
        format: Texture2D.PixelFormat.RGBA8888,
        _compressed: false,
    });
    return imageAsset;
}

export default abstract class ITintShaderManipulator extends BaseShaderManipulator implements ITintObject {
    tintGradient: Gradient;
    useOriginColor: boolean;
    premultipliedAlpha: boolean;
    grayValueOffset: number;
    useSampleBound: boolean;
    sampleLeftBound: number;
    sampleRightBound: number;
}
export interface ITintObject {
    tintGradient: Gradient;
    useOriginColor: boolean;
    premultipliedAlpha: boolean;
    grayValueOffset: number;
    useSampleBound: boolean;
    sampleLeftBound: number;
    sampleRightBound: number;
}

export namespace Tint {
    export function getGradientPixelsBuffer(
        gradient: Gradient | Gradient[],
        textureWidth: number = 100,
        useSampleBound: boolean = false,
        sampleLeftBound: number = 0,
        sampleRightBound: number = 1,
    ): number[] {
        if (gradient instanceof Gradient) {
            gradient = [gradient];
        } else {
            textureWidth = 100;
            useSampleBound = false;
            sampleLeftBound = 0;
            sampleRightBound = 1;
        }
        const tempArr: number[] = [];
        for (let gradientIndex = 0; gradientIndex < gradient.length; gradientIndex++) {
            let firstColorKey: ColorKey;
            let lastColorKey: ColorKey;
            let firstAlphaKey: AlphaKey;
            let lastAlphaKey: AlphaKey;
            if (gradient[gradientIndex].colorKeys.length > 0) {
                firstColorKey = gradient[gradientIndex].colorKeys[0];
                lastColorKey = gradient[gradientIndex].colorKeys[gradient[gradientIndex].colorKeys.length - 1];
            }
            if (gradient[gradientIndex].alphaKeys.length > 0) {
                firstAlphaKey = gradient[gradientIndex].alphaKeys[0];
                lastAlphaKey = gradient[gradientIndex].alphaKeys[gradient[gradientIndex].alphaKeys.length - 1];
            }
            const sampleStart = useSampleBound ? Math.floor(sampleLeftBound * textureWidth) : 0;
            let sampleBound = useSampleBound ? Math.ceil(sampleRightBound * textureWidth) : textureWidth;
            if (sampleStart > sampleBound) {
                sampleBound = sampleStart + 1;
            }
            for (let i = sampleStart; i <= sampleBound; i++) {
                // 把最後面顏色也算進去
                const tempColor = gradient[gradientIndex].evaluate(Math.min(0.99999, i / textureWidth));
                // 取代掉邊界沒設定顏色或透明的Engine預設值
                if (gradient[gradientIndex].colorKeys.length > 0) {
                    if (i / textureWidth < firstColorKey.time) {
                        tempColor.r = firstColorKey.color.r;
                        tempColor.g = firstColorKey.color.g;
                        tempColor.b = firstColorKey.color.b;
                    } else if (i / textureWidth > lastColorKey.time) {
                        tempColor.r = lastColorKey.color.r;
                        tempColor.g = lastColorKey.color.g;
                        tempColor.b = lastColorKey.color.b;
                    }
                }
                if (gradient[gradientIndex].alphaKeys.length > 0) {
                    if (i / textureWidth < firstAlphaKey.time) {
                        tempColor.a = firstAlphaKey.alpha;
                    } else if (i / textureWidth > lastAlphaKey.time) {
                        tempColor.a = lastAlphaKey.alpha;
                    }
                }
                tempArr.push(tempColor.r);
                tempArr.push(tempColor.g);
                tempArr.push(tempColor.b);
                tempArr.push(tempColor.a);
            }
        }
        return tempArr;
    }
    // TODO: Gradient 外的參數應該也要是陣列，否則參數有不同的話會有問題，Ex 圖片大小
    //       或是多組 Gradient 時，其他參數直接使用預設
    export function assignGradientTexture(
        targetTexture: Texture2D,
        gradient: Gradient | Gradient[],
        textureWidth: number = 100,
        useSampleBound: boolean = false,
        sampleLeftBound: number = 0,
        sampleRightBound: number = 1,
    ) {
        const gradientArrayLength = gradient instanceof Gradient ? 1 : gradient.length;
        const gradientColorArr = getGradientPixelsBuffer(gradient, textureWidth, useSampleBound, sampleLeftBound, sampleRightBound);
        targetTexture.image = generateGradientImage(gradientColorArr, gradientArrayLength);
        targetTexture.setWrapMode(Texture2D.WrapMode.CLAMP_TO_EDGE, Texture2D.WrapMode.CLAMP_TO_EDGE);
        targetTexture.serialize();
    }

    export function synthesisGradientTexture(targetTexture: Texture2D, gradientColorArrays: number[][]) {
        // 需確保長度一致
        let maxLength = 0;
        gradientColorArrays.forEach((colorArray) => {
            maxLength = Math.max(maxLength, colorArray.length);
        });

        let gradientColorArr = [];
        gradientColorArrays.forEach((colorArray) => {
            if (colorArray.length < maxLength) {
                for (let i = colorArray.length; i < maxLength; i += 4) {
                    colorArray.push(1);
                    colorArray.push(1);
                    colorArray.push(1);
                    colorArray.push(1);
                }
                if (colorArray.length !== maxLength) {
                    error('Tint synthesisGradientTexture: Color Buffer 長度不一致');
                }
            }
            gradientColorArr = gradientColorArr.concat(colorArray);
        });
        targetTexture.image = generateGradientImage(gradientColorArr, gradientColorArrays.length);
        targetTexture.setWrapMode(Texture2D.WrapMode.CLAMP_TO_EDGE, Texture2D.WrapMode.CLAMP_TO_EDGE);
        targetTexture.serialize();
    }

    export function updateGradientProperty(pass: renderer.Pass, texture: Texture2D) {
        if (!pass) {
            return;
        }
        // 因為 materialInstance 的關係，如果要用 setProperty 要設在 renderer.material 上，不是設在 customMaterial 上。
        pass.bindTexture(pass.getBinding('gradientSprite'), texture.getGFXTexture());
        pass.bindSampler(pass.getBinding('gradientSprite'), texture.getGFXSampler());
    }

    // 選取是否要採用原圖顏色。
    export function updateUseOriginColorProperty(pass: renderer.Pass, useOriginColor: boolean) {
        if (!pass) {
            return;
        }
        pass.setUniform(pass.getHandle('useOriginColor'), +useOriginColor);
    }

    // 選取是否要採用預乘Alpha。
    export function updatePremultipliedAlphaProperty(pass: renderer.Pass, premultipliedAlpha: boolean) {
        if (!pass) {
            return;
        }
        pass.setUniform(pass.getHandle('premultipliedAlpha'), +premultipliedAlpha);
    }

    export function updateGrayValueOffsetProperty(pass: renderer.Pass, grayValueOffset: number) {
        if (!pass) {
            return;
        }
        pass.setUniform(pass.getHandle('grayValueOffset'), clamp01(grayValueOffset));
    }

    export function showSampleColor(sampleSprite: Sprite, texture: Texture2D) {
        if (sampleSprite?.node) {
            const tempSprite = SpriteFrame.createWithImage(texture.image);
            tempSprite.packable = false;
            sampleSprite.spriteFrame = tempSprite;
            sampleSprite.node.getComponent(UITransform).setContentSize(100, 100);
        }
    }
}
